import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('MIXPANEL_IDB_GUARD');

const MIXPANEL_DB_NAME = 'mixpanelBrowserDb';
const RECORDING_EVENTS_STORE = 'mixpanelRecordingEvents';
const RECORDING_REGISTRY_STORE = 'mixpanelRecordingRegistry';
const REQUIRED_STORES = [RECORDING_EVENTS_STORE, RECORDING_REGISTRY_STORE];

// If the buffered-events store grows beyond this, we purge it to prevent
// unbounded growth on long-lived sessions.
const MAX_BUFFERED_EVENTS = 300;

// Regex matching the unhandled rejection message produced by mixpanel-browser
// when the persisted DB schema is missing a required object store. The exact
// wording is browser-specified and stable across Chromium/Firefox/Safari.
const MIXPANEL_IDB_NOT_FOUND_RE =
  /Failed to execute 'transaction' on 'IDBDatabase': One of the specified object stores was not found/i;

function deleteMixpanelDb(reason: string): Promise<void> {
  return new Promise((resolve) => {
    log.warn(`Deleting ${MIXPANEL_DB_NAME} (${reason})`);
    const req = indexedDB.deleteDatabase(MIXPANEL_DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => {
      log.error(`deleteDatabase ${MIXPANEL_DB_NAME} failed`, req.error);
      resolve();
    };
    req.onblocked = () => {
      log.warn(`deleteDatabase ${MIXPANEL_DB_NAME} blocked — continuing anyway`);
      resolve();
    };
  });
}

/**
 * Inspect the persisted mixpanel IndexedDB and repair it if needed.
 *
 * Mixpanel's session-replay module (mixpanel-browser >= 2.50) opens
 * `mixpanelBrowserDb` at a hardcoded DB_VERSION=1. If the version on disk
 * already matches but the object stores don't (because an older mixpanel build
 * created the DB with a different schema), `onupgradeneeded` never fires and
 * every `transaction()` call inside the SDK throws `NotFoundError`. Since the
 * SDK wraps those calls in uncaught promise chains, the rejection becomes an
 * `Uncaught (in promise)` that fires on every recording start/stop cycle.
 *
 * Call this BEFORE `mixpanel.init()` so the SDK can recreate the DB fresh.
 */
export async function sanitizeMixpanelIdb(): Promise<void> {
  if (typeof indexedDB === 'undefined') return;

  await new Promise<void>((resolve) => {
    let openReq: IDBOpenDBRequest;
    try {
      openReq = indexedDB.open(MIXPANEL_DB_NAME);
    } catch (err) {
      log.error('indexedDB.open threw', err);
      resolve();
      return;
    }

    openReq.onerror = () => {
      log.error(`Could not open ${MIXPANEL_DB_NAME}`, openReq.error);
      resolve();
    };

    openReq.onsuccess = () => {
      const db = openReq.result;

      const missing = REQUIRED_STORES.filter((s) => !db.objectStoreNames.contains(s));
      if (missing.length > 0) {
        db.close();
        deleteMixpanelDb(`stale schema (missing: ${missing.join(', ')})`).then(resolve);
        return;
      }

      try {
        const countReq = db.transaction(RECORDING_EVENTS_STORE, 'readonly')
          .objectStore(RECORDING_EVENTS_STORE).count();
        countReq.onsuccess = () => {
          const count = countReq.result;
          db.close();
          if (count > MAX_BUFFERED_EVENTS) {
            deleteMixpanelDb(`oversized buffer (${count} events)`).then(resolve);
          } else {
            resolve();
          }
        };
        countReq.onerror = () => {
          log.error('count() on recording events store failed', countReq.error);
          db.close();
          resolve();
        };
      } catch (err) {
        log.error('Unexpected error counting recording events', err);
        try { db.close(); } catch { /* noop */ }
        resolve();
      }
    };
  });
}

let rejectionGuardInstalled = false;

/**
 * Install a window-level `unhandledrejection` listener that silently swallows
 * the specific `NotFoundError` thrown by mixpanel-browser's IndexedDB wrapper
 * when its schema is stale. All other rejections pass through untouched.
 *
 * This is a safety net — `sanitizeMixpanelIdb()` should prevent the error
 * from occurring in the first place, but the SDK has several independent code
 * paths (session recording, registry, batcher) that can each surface the
 * rejection in ways the SDK does not catch. Installing this listener ensures
 * a transient schema mismatch never produces user-visible noise or flood
 * Sentry/console.
 *
 * Must be called synchronously at startup, before the mixpanel bundle loads.
 */
export function installMixpanelRejectionGuard(): void {
  if (rejectionGuardInstalled) return;
  if (typeof window === 'undefined') return;
  rejectionGuardInstalled = true;

  window.addEventListener('unhandledrejection', (event) => {
    const message = String(event.reason?.message ?? event.reason ?? '');
    if (MIXPANEL_IDB_NOT_FOUND_RE.test(message)) {
      event.preventDefault();
      log.debug('Suppressed mixpanel IDB NotFoundError');
    }
  });
}
